import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { canManageSchool, useOrgMe } from "@/api/school";
import { useAuth } from "@/auth/OidcProvider";
import { searchableTwinOf } from "@/lib/searchableTwin";
import {
  formatFileTime,
  libraryFilesApi,
  sortFilesByTimeDesc,
  type DocTreeFile,
} from "../teachingPlans/documentsApi";

/** 集团知识库(院所信息)—— 园长/集团管理员专用页。
 *
 *  这里放"全体教职工都该问得到的东西":院所介绍、规章制度、校历、通用话术。
 *  传上去即进 `default` 集合。
 *
 *  老师**不进这个页面**:他们在聊天里检索得到这些内容(靠 `default` 上的
 *  role:operator read 授权),但拿不到文件本身。Jarod 2026-09-01 的口径 ——
 *  「在聊天里可以读取集团 default 知识库的内容,但是不能直接打开阅读该文件」。
 *  后端四个端点同样要求园长,这里的门只是少一次 403。
 *
 *  学科资料不要放这里 —— 那是各学科库(/subjects),放这里等于对全校公开。 */
export default function LibraryPage() {
  const { t } = useTranslation();
  const { state } = useAuth();
  const isAdmin = state.status === "authenticated" && state.user.isAdmin;
  const orgMe = useOrgMe();
  const canManage = isAdmin || canManageSchool(orgMe.data);

  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const tree = useQuery({
    queryKey: ["library-files"],
    queryFn: () => libraryFilesApi.list(),
    enabled: canManage,
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) {
        await libraryFilesApi.upload(f.name, f);
        // ppt/doc 等不可检索格式 → 生成 PDF 孪生一起入库,否则内容检索不到
        // (Jarod 2026-08-31 报过同样的坑)。原件保留,供下载/预览。
        const twin = await searchableTwinOf(f);
        if (twin) await libraryFilesApi.upload(twin.name, twin);
      }
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["library-files"] }),
  });

  const remove = useMutation({
    mutationFn: (name: string) => libraryFilesApi.remove(name),
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["library-files"] }),
  });

  const download = async (name: string) => {
    setError(null);
    try {
      const blob = await libraryFilesApi.download(name);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const files: DocTreeFile[] = sortFilesByTimeDesc(tree.data?.files ?? []);

  if (!canManage) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">{t("library.title")}</h1>
        <p className="text-sm text-gray-500 mt-2">{t("library.managerOnly")}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{t("library.title")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("library.subtitle")}</p>
        </div>
        <div>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const fs = Array.from(e.target.files ?? []);
                if (fs.length) upload.mutate(fs);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              disabled={upload.isPending}
              onClick={() => inputRef.current?.click()}
              className="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm disabled:opacity-50"
            >
              {upload.isPending ? t("library.uploading") : t("library.upload")}
            </button>
        </div>
      </header>

      {error && (
        <div className="rounded-md bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>
      )}

      {tree.isLoading ? (
        <p className="text-sm text-gray-500">{t("library.loading")}</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-gray-500">
          {t("library.emptyManager")}
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {files.map((f) => (
            <li key={f.name} className="flex items-center gap-3 px-3 py-2">
              <span className="flex-1 truncate text-sm">{f.name}</span>
              {f.status === "ingested" && (
                <span className="text-xs text-green-600 shrink-0">{t("library.indexed")}</span>
              )}
              <span className="text-xs text-gray-400 shrink-0">{formatFileTime(f.created_at)}</span>
              <button
                type="button"
                onClick={() => void download(f.name)}
                className="text-xs text-indigo-600 shrink-0"
              >
                {t("library.download")}
              </button>
              <button
                type="button"
                onClick={() => remove.mutate(f.name)}
                className="text-xs text-red-600 shrink-0"
              >
                {t("library.delete")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
